"use client"

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import { lockBodyScroll, unlockBodyScroll } from "@/lib/dom/scrollLock"
import { isIOS } from "@/lib/viewport/isIOS"
import { useFocusTrap } from "../popup/hooks/useFocusTrap"

interface BaseBottomSheetStore {
  isOpen: boolean
  content: React.ReactNode
  naviHeight: number | null
  isBackground: boolean | null
  closeSheet: () => void
}

interface BaseBottomSheetPortalProps {
  store: BaseBottomSheetStore
  zIndex?: string
}

export default function BaseBottomSheetPortal({
  store,
  zIndex = "z-10000",
}: BaseBottomSheetPortalProps) {
  const { isOpen, content, closeSheet, naviHeight, isBackground } = store
  const [mounted, setMounted] = useState(false)
  const { containerRef, handleAnimationComplete } = useFocusTrap({
    isOpen,
    onClose: closeSheet,
  })

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (typeof document === "undefined") return
    const iosFixEnabled = process.env.NEXT_PUBLIC_IOS_VIEWPORT_FIX === "1"
    const shouldLock = iosFixEnabled && isIOS()

    if (shouldLock) {
      // 닫힌 상태에서는 unlock을 호출하지 않는다. effect cleanup은
      // false -> true 전환보다 먼저 실행되므로, 여기서 unlock하면 저장 전
      // 초기 스크롤 위치(0)로 이동할 수 있다.
      if (!isOpen) return

      lockBodyScroll()
      return () => {
        unlockBodyScroll()
      }
    }

    document.body.style.overflow = isOpen ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  if (!mounted) return null

  const overlayRoot = document.getElementById("overlay-root")
  if (!overlayRoot || !mounted) return null

  return createPortal(
    <AnimatePresence>
      {isOpen &&
        (isBackground ? (
          <>
            <motion.div
              className={`pointer-events-auto fixed inset-0 ${zIndex} h-vh flex touch-none flex-col items-center overscroll-y-none`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeSheet}
            >
              <motion.div
                ref={containerRef}
                tabIndex={-1}
                className={`${naviHeight || naviHeight === 0 ? `pt-[${naviHeight}px]` : "tp-navibar-height"} relative flex h-full w-full max-w-3xl flex-1 flex-col`}
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "100%", opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onAnimationComplete={handleAnimationComplete}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bottom-0 z-1 h-full">{content}</div>
                <div className="absolute -bottom-10 z-0 h-full w-full bg-gradient-to-b from-black/0 to-black/80"></div>
              </motion.div>
            </motion.div>
          </>
        ) : (
          <>
            <motion.div
              className={`pointer-events-auto fixed inset-0 ${zIndex} h-vh flex touch-none flex-col items-center overscroll-y-none bg-black/40`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeSheet}
            >
              <motion.div
                ref={containerRef}
                tabIndex={-1}
                className={`${naviHeight || naviHeight === 0 ? `pt-[${naviHeight}px]` : "tp-navibar-height"} relative flex h-full w-full max-w-3xl flex-1 flex-col`}
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "100%", opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onAnimationComplete={handleAnimationComplete}
                onClick={(e) => e.stopPropagation()}
              >
                {content}
                <div className="absolute -bottom-10 h-10 w-full bg-rl-common-white"></div>
              </motion.div>
            </motion.div>
          </>
        ))}
    </AnimatePresence>,
    overlayRoot as Element,
  )
}
